/**
 * UranIA PA — Dashboard widget: Checklist beneficiario R&I.
 *
 * Visibile solo quando il principal ha ruolo ``beneficiario_ri``.
 * Mostra una sequenza guidata a 7 step "cosa fare in UranIA come
 * ricercatore", con stato (done/todo/roadmap) e link diretti alle
 * pagine corrispondenti (onboarding, chat-prova, banche dati).
 *
 * Boundary:
 *   - Step 1 (SPID identificato) e' sempre done se l'utente vede il
 *     widget (e' autenticato).
 *   - Step 2 (onboarding R&I) e' "todo" di default: la registrazione
 *     dello stato profilo via API ``GET /api/v1/user-profiles/ri/me``
 *     non e' ancora implementata, quindi lo step diventa "done" solo
 *     quando l'utente clicca "Completa onboarding" e torna alla
 *     dashboard (state locale TanStack invalidato al return).
 *   - Step 3-5 sono link verso ``/chat-prova`` con prompt template
 *     pre-compilato (skill_hint = skill_programmazione_coesione,
 *     skill_verifica_gber, skill_decisore_fase_b).
 *   - Step 6-7 sono ROADMAP Fase 4 (workspace R&I + integrazioni
 *     CORDIS/PN-RIC): badge giallo, non cliccabili.
 */

import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import type { JSX } from 'react';

import { selectPrincipal, useAuthStore } from '../../lib/auth/auth-store.ts';
import { principalIsBeneficiario } from '../../lib/auth/rbac.ts';

type StepStatus = 'done' | 'todo' | 'roadmap';

interface ChecklistStep {
  readonly key: string;
  readonly status: StepStatus;
  readonly to: string | null;
}

function IconDone(): JSX.Element {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 16 16"
      fill="#198754"
      aria-hidden="true"
    >
      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
    </svg>
  );
}

function IconTodo(): JSX.Element {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 16 16"
      fill="none"
      stroke="#6c757d"
      strokeWidth="1"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="7.5" />
    </svg>
  );
}

function IconRoadmap(): JSX.Element {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 16 16"
      fill="#856404"
      aria-hidden="true"
    >
      <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zM3.5 7.5a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 0 1H4a.5.5 0 0 1-.5-.5z" />
    </svg>
  );
}

const STEPS: readonly ChecklistStep[] = [
  { key: 'spid', status: 'done', to: null },
  { key: 'onboarding', status: 'todo', to: '/onboarding' },
  { key: 'explore_manuale', status: 'todo', to: '/chat-prova' },
  { key: 'calcolo_gber', status: 'todo', to: '/chat-prova' },
  { key: 'dnsh', status: 'todo', to: '/chat-prova' },
  { key: 'proposal', status: 'roadmap', to: null },
  { key: 'bandi_attivi', status: 'roadmap', to: null },
];

function StepIcon(props: { readonly status: StepStatus }): JSX.Element {
  if (props.status === 'done') return <IconDone />;
  if (props.status === 'roadmap') return <IconRoadmap />;
  return <IconTodo />;
}

export function DashboardRiChecklistWidget(): JSX.Element | null {
  const { t } = useTranslation();
  const principal = useAuthStore(selectPrincipal);

  if (!principalIsBeneficiario(principal)) {
    return null;
  }

  const doneCount = STEPS.filter((s) => s.status === 'done').length;
  const totalCount = STEPS.length;
  const progressPct = Math.round((doneCount / totalCount) * 100);

  return (
    <article
      className="urania-dashboard__widget card h-100 shadow-sm border-0"
      data-testid="dashboard-widget-ri-checklist"
      aria-labelledby="urania-dashboard-ri-checklist-heading"
    >
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <h2
            id="urania-dashboard-ri-checklist-heading"
            className="h5 card-title mb-0"
          >
            {t('dashboard.ri_checklist.title')}
          </h2>
          <span
            className="badge"
            style={{
              backgroundColor: '#e7f1ff',
              color: '#084298',
              border: '1px solid #b6d4fe',
              fontSize: '0.7rem',
            }}
          >
            {doneCount} / {totalCount}
          </span>
        </div>
        <p className="text-muted small mb-3">
          {t('dashboard.ri_checklist.subtitle')}
        </p>

        <div className="mb-3">
          <div
            className="progress"
            style={{ height: 6, backgroundColor: '#e9ecef' }}
            role="progressbar"
            aria-valuenow={progressPct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="progress-bar"
              style={{ width: `${progressPct}%`, backgroundColor: '#0066cc' }}
            />
          </div>
        </div>

        <ul
          className="list-unstyled mb-0 d-flex flex-column gap-2"
          aria-label={t('dashboard.ri_checklist.aria_label')}
        >
          {STEPS.map((step, idx) => {
            const label = t(`dashboard.ri_checklist.steps.${step.key}.label`);
            const desc = t(`dashboard.ri_checklist.steps.${step.key}.description`);
            return (
              <li
                key={step.key}
                className="d-flex gap-2 align-items-start"
                data-testid={`ri-checklist-step-${step.key}`}
              >
                <span className="flex-shrink-0 mt-1">
                  <StepIcon status={step.status} />
                </span>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                    <span
                      className={`small fw-semibold ${
                        step.status === 'done' ? 'text-success' : ''
                      } ${step.status === 'roadmap' ? 'text-muted' : ''}`}
                    >
                      {idx + 1}. {label}
                    </span>
                    {step.status === 'roadmap' && (
                      <span
                        className="badge"
                        style={{
                          backgroundColor: '#fff3cd',
                          color: '#856404',
                          border: '1px solid #ffeaa7',
                          fontSize: '0.62rem',
                        }}
                      >
                        ROADMAP FASE 4
                      </span>
                    )}
                  </div>
                  <p className="small text-muted mb-0 lh-sm" style={{ fontSize: '0.78rem' }}>
                    {desc}
                  </p>
                  {step.to !== null && step.status !== 'done' ? (
                    <Link
                      to={step.to}
                      className="small text-decoration-none"
                      style={{ fontSize: '0.78rem' }}
                      data-testid={`ri-checklist-step-${step.key}-link`}
                    >
                      → {t('dashboard.ri_checklist.cta_open')}
                    </Link>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </article>
  );
}
